import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Stack, Typography, Paper, Box, Button, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Link,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { apiGet, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

function fmtDateTime(v) {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "number") {
    const ms = v > 1e12 ? v : v * 1000;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function StatusChip({ status }) {
  const v = String(status || "").toLowerCase();
  if (v === "scheduled") return <Chip size="small" label="Scheduled" color="primary" variant="outlined" />;
  if (v === "fired" || v === "succeeded") return <Chip size="small" label="Fired" color="success" variant="outlined" />;
  if (v === "cancelled" || v === "canceled") return <Chip size="small" label="Cancelled" variant="outlined" />;
  if (v === "skipped") return <Chip size="small" label="Skipped" color="warning" variant="outlined" />;
  if (v === "failed" || v === "error") return <Chip size="small" label="Failed" color="error" variant="outlined" />;
  return <Chip size="small" label={status || "—"} variant="outlined" />;
}

const COLUMNS = [
  { key: "status",      header: "Status",     width: 130 },
  { key: "trigger_at",  header: "Trigger At", width: 200 },
  { key: "title",       header: "Title",      width: undefined },
  { key: "amount",      header: "Bid",        width: 90,  align: "right" },
  { key: "insurance",   header: "Insurance",  width: 100, align: "center" },
  { key: "location",    header: "Location",   width: 130 },
  { key: "product_id",  header: "Product",    width: 140 },
  { key: "fired_at",    header: "Fired At",   width: 200 },
];

export default function Scheduled() {
  const { error: toastError } = useToast();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null); // null = "all"
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async (signal) => {
    setLoading(true);
    try {
      const js = await apiGet("/api/wizbid/scheduled", { signal });
      const data = Array.isArray(js?.results) ? js.results : Array.isArray(js) ? js : [];
      setRows(data.map((r) => ({
        id: r.id,
        auction_id: r.auction_id,
        product_id: r.product_id,
        title: r.title || "",
        location: r.location || "",
        condition: r.condition || "",
        amount: r.amount,
        trigger_at: r.trigger_at,
        fired_at: r.fired_at,
        cancelled_at: r.cancelled_at,
        status: r.status || "(Unknown)",
        insurance: !!r.insurance,
      })));
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load scheduled bids", e instanceof ApiError ? e.message : String(e));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchRows(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchRows]);

  const counts = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const s = String(r.status || "(Unknown)");
      m[s] = (m[s] || 0) + 1;
    }
    return m;
  }, [rows]);

  const visible = useMemo(() => {
    const arr = !statusFilter ? rows : rows.filter((r) => r.status === statusFilter);
    return [...arr].sort((a, b) => {
      const av = a.trigger_at ?? null;
      const bv = b.trigger_at ?? null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      const an = typeof av === "number" ? av : Date.parse(av);
      const bn = typeof bv === "number" ? bv : Date.parse(bv);
      return (an || 0) - (bn || 0);
    });
  }, [rows, statusFilter]);

  const allCount = rows.length;

  return (
    <Box>
      {/* Page header — matches OC's standard layout */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Scheduled Bids
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {visible.length} of {allCount} shown
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => fetchRows()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Status filter chips */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            label={`All (${allCount})`}
            color={!statusFilter ? "primary" : "default"}
            variant={!statusFilter ? "filled" : "outlined"}
            onClick={() => setStatusFilter(null)}
          />
          {Object.entries(counts).map(([s, n]) => (
            <Chip
              key={s}
              size="small"
              label={`${s} (${n})`}
              color={statusFilter === s ? "primary" : "default"}
              variant={statusFilter === s ? "filled" : "outlined"}
              onClick={() => setStatusFilter(statusFilter === s ? null : s)}
            />
          ))}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <CalendarTodayOutlinedIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No scheduled bids</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {visible.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ width: 130 }}>
                    <StatusChip status={r.status} />
                  </TableCell>
                  <TableCell sx={{ width: 200, color: 'text.secondary' }}>
                    {fmtDateTime(r.trigger_at) || "—"}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 0, maxWidth: 360 }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.title || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 90, fontVariantNumeric: 'tabular-nums' }}>
                    ${Number(r.amount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>
                    {r.insurance ? (
                      <Chip
                        size="small"
                        icon={<ShieldOutlinedIcon sx={{ fontSize: 14 }} />}
                        label="On"
                        color="success"
                        variant="outlined"
                        title="Buyer's insurance enabled"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary" title="Buyer's insurance not enabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 130, color: r.location ? 'text.primary' : 'text.secondary' }}>
                    {r.location || "—"}
                  </TableCell>
                  <TableCell sx={{ width: 140 }}>
                    {r.product_id ? (
                      <Link
                        href={`https://wiz.bid/productView/${r.product_id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          fontFamily: 'mono',
                          fontSize: '0.75rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {r.product_id}
                        <OpenInNewOutlinedIcon sx={{ fontSize: 12 }} />
                      </Link>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 200, color: 'text.secondary' }}>
                    {fmtDateTime(r.fired_at) || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && visible.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
      </Paper>
    </Box>
  );
}
